import clsx from "clsx";

import { normalizeCitationMarkerIndices } from "@/utils/chatMockup";

import { MockupFrame, WindowDots } from "./MarketingChannelMockups";
import { RevealItem, RevealSequence } from "./RevealSequence";

export type StudioChatMessage = {
  role: "user" | "assistant";
  text: string;
  /** Tool/source tag shown below assistant messages (e.g. policies.db). */
  tag?: string;
  /** Citation indices rendered as superscript markers after the message text. */
  citationMarkers?: number[];
};

export type StudioChatCitation = {
  index: number;
  label: string;
  type?: string;
};

export type StudioChatMockupProps = {
  header: string;
  messages: StudioChatMessage[];
  citations?: StudioChatCitation[];
};

function FrameHeader({ label }: Readonly<{ label: string }>) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-black/5 px-3 py-2">
      <WindowDots />
      <span className="truncate font-monument text-[0.625rem] text-black/70">
        {label}
      </span>
    </div>
  );
}

function CiteMarker({ index }: Readonly<{ index: number }>) {
  return (
    <sup className="ml-0.5 align-super font-monument text-[0.5rem] font-medium text-gail-primary">
      [{index}]
    </sup>
  );
}

export function StudioChatMockup({
  header,
  messages,
  citations = [],
}: Readonly<StudioChatMockupProps>) {
  const hasCitations = citations.length > 0;

  return (
    <MockupFrame rounded="all">
      <FrameHeader label={header} />
      <RevealSequence className="flex-1 space-y-2.5 p-3">
        {messages.map((message, index) => (
          <RevealItem
            key={`${message.role}-${index}`}
            order={index}
            className={clsx(
              message.role === "user" && "flex justify-end",
            )}
          >
            {message.role === "user" ? (
              <p className="max-w-[88%] rounded-xl rounded-br-sm bg-gail-primary px-3 py-2 font-monument text-xs leading-snug text-white">
                {message.text}
              </p>
            ) : (
              <div>
                <p className="font-monument text-xs leading-snug text-black/80">
                  {message.text}
                  {normalizeCitationMarkerIndices(message.citationMarkers)?.map(
                    (marker, markerIndex) => (
                      <CiteMarker
                        key={`cite-${index}-${markerIndex}-${marker}`}
                        index={marker}
                      />
                    ),
                  )}
                </p>
                {message.tag ? (
                  <span className="mt-2 inline-block rounded-md bg-gail-primary/10 px-1.5 py-0.5 font-mono text-[0.625rem] text-gail-primary">
                    {message.tag}
                  </span>
                ) : null}
              </div>
            )}
          </RevealItem>
        ))}
        {hasCitations ? (
          <div className="space-y-1.5 border-t border-black/5 pt-2">
            {citations.map((citation, citationIndex) => (
              <RevealItem
                key={`citation-${citationIndex}-${citation.index}`}
                order={messages.length + citationIndex}
                className="flex items-baseline gap-1.5 font-monument text-[0.625rem]"
              >
                <span className="font-medium text-gail-primary">
                  [{citation.index}]
                </span>
                <span className="text-black/70">{citation.label}</span>
                {citation.type ? (
                  <span className="text-black/70">{citation.type}</span>
                ) : null}
              </RevealItem>
            ))}
          </div>
        ) : null}
      </RevealSequence>
    </MockupFrame>
  );
}
